class DogAvenuePressQuotes extends HTMLElement {
	connectedCallback() {
		this.quoteEls = this.querySelectorAll('[data-press-quote]');
		this.logoEls = this.querySelectorAll('[data-press-logo]');
		this.fakeEls = this.querySelectorAll('[data-press-fake]');
		this.viewport = this.querySelector('[data-spotlight-viewport]');
		this.track = this.querySelector('[data-spotlight-track]');
		this.n = this.quoteEls.length;
		if (this.n === 0) return;

		this._tx = 0;
		this._setWidth = 0;
		this._lastTs = 0;
		this._rafId = 0;
		this._running = false;
		this._lastActiveUpdate = 0;
		this._activeThrottleMs = 120;

		this._onVis = this._onVisibilityChange.bind(this);
		this._onResize = this._debounce(this._onResize.bind(this), 175);
		this._tick = this._tick.bind(this);

		window.addEventListener('resize', this._onResize);

		this.setActive(0);
		this._bindLogoClicks();

		if (!this.viewport || !this.track) return;

		requestAnimationFrame(() => {
			this._measureSetWidth();
			this._tx = 0;
			this.track.style.transition = 'none';
			this.track.style.transform = 'translate3d(0, 0, 0)';

			if (this.n < 2 || this._reducedMotion) return;

			document.addEventListener('visibilitychange', this._onVis);
			this._startMarquee();
			if (this._setWidth <= 0) {
				window.setTimeout(() => {
					this._measureSetWidth();
					if (this._setWidth > 0 && !this._reducedMotion) this._startMarquee();
				}, 200);
			}
		});
	}

	get _reducedMotion() {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	_marqueeSpeedPxPerSec() {
		const v = parseFloat(this.dataset.marqueeSpeed) || 40;
		return Math.min(120, Math.max(15, v));
	}

	_measureSetWidth() {
		if (!this.fakeEls.length || !this.logoEls.length) {
			this._setWidth = 0;
			return;
		}
		this._setWidth = this.fakeEls[0].offsetLeft - this.logoEls[0].offsetLeft;
		if (!Number.isFinite(this._setWidth) || this._setWidth <= 0) {
			this._setWidth = 0;
		}
	}

	_startMarquee() {
		if (this._running || this._rafId) return;
		if (this._setWidth <= 0) {
			this._measureSetWidth();
			if (this._setWidth <= 0) return;
		}
		this._running = true;
		this._lastTs = 0;
		this._rafId = requestAnimationFrame(this._tick);
	}

	_stopMarquee() {
		this._running = false;
		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
			this._rafId = 0;
		}
	}

	_tick(ts) {
		this._rafId = 0;
		if (!this._running || !this.track) return;

		if (!this._lastTs) this._lastTs = ts;
		const dt = Math.min(0.064, (ts - this._lastTs) / 1000);
		this._lastTs = ts;

		const speed = this._marqueeSpeedPxPerSec();
		this._tx -= speed * dt;

		while (this._setWidth > 0 && this._tx <= -this._setWidth) {
			this._tx += this._setWidth;
		}

		this.track.style.transition = 'none';
		this.track.style.transform = `translate3d(${this._tx}px, 0, 0)`;

		const now = typeof performance !== 'undefined' ? performance.now() : ts;
		if (now - this._lastActiveUpdate >= this._activeThrottleMs) {
			this._lastActiveUpdate = now;
			this._updateActiveFromCenter();
		}

		if (this._running) this._rafId = requestAnimationFrame(this._tick);
	}

	_updateActiveFromCenter() {
		if (!this.viewport || !this.n) return;
		const v = this.viewport.getBoundingClientRect();
		const cx = v.left + v.width / 2;
		let best = 0;
		let bestDist = Infinity;
		const els = this.viewport.querySelectorAll('[data-logo-index]');
		els.forEach((el) => {
			const r = el.getBoundingClientRect();
			if (r.width <= 0) return;
			const mid = r.left + r.width / 2;
			const d = Math.abs(mid - cx);
			if (d < bestDist) {
				bestDist = d;
				const idx = parseInt(el.getAttribute('data-logo-index'), 10);
				if (!Number.isNaN(idx)) best = idx;
			}
		});
		if (best !== this.i) this.setActive(best);
	}

	_bindLogoClicks() {
		this.logoEls.forEach((btn) => {
			btn.addEventListener('click', () => {
				const idx = parseInt(btn.getAttribute('data-logo-index'), 10);
				if (!Number.isNaN(idx)) this.setActive(idx);
			});
		});
	}

	disconnectedCallback() {
		this._stopMarquee();
		document.removeEventListener('visibilitychange', this._onVis);
		window.removeEventListener('resize', this._onResize);
	}

	_onVisibilityChange() {
		if (document.hidden) {
			this._stopMarquee();
		} else if (this.n > 1 && !this._reducedMotion) {
			this._measureSetWidth();
			this._startMarquee();
		}
	}

	_onResize() {
		if (!this.viewport || !this.track || !this.logoEls.length) return;
		this._measureSetWidth();
		if (this._setWidth > 0) {
			this._tx = this._tx % this._setWidth;
			if (this._tx > 0) this._tx -= this._setWidth;
		}
		this.track.style.transition = 'none';
		this.track.style.transform = `translate3d(${this._tx}px, 0, 0)`;
		this._updateActiveFromCenter();
	}

	_setActiveVisual(index) {
		if (!this.viewport) return;
		const els = this.viewport.querySelectorAll('[data-logo-index]');
		els.forEach((el) => {
			const idx = parseInt(el.getAttribute('data-logo-index'), 10);
			if (Number.isNaN(idx)) return;
			const on = idx === index;
			el.classList.toggle('is-active', on);
			if (el.tagName === 'BUTTON') {
				el.setAttribute('aria-pressed', on ? 'true' : 'false');
			}
		});
	}

	setActive(index) {
		const j = Math.max(0, Math.min(this.n - 1, index));
		this.i = j;
		this.quoteEls.forEach((el, k) => {
			const on = k === j;
			el.classList.toggle('is-active', on);
			el.toggleAttribute('hidden', !on);
		});
		this._setActiveVisual(j);
	}

	_debounce(fn, ms) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), ms);
		};
	}
}

if (!window.customElements.get('dog-avenue-press-quotes')) {
	window.customElements.define('dog-avenue-press-quotes', DogAvenuePressQuotes);
}
